/**
 * Renderowanie treści powiadomienia po stronie klienta.
 *
 * Faza 8 modelu kluczy firmowych (docs/model-kluczy-firmowych.md): BE dawniej zapisywał
 * do Notification.Message gotowy, jawny tekst wygenerowany z szablonu (tytuły spraw,
 * sygnatury, nazwiska). Teraz BE wysyła MessageKey (nazwa NotificationReason) +
 * MessageArgsJson (płaski obiekt string→string, dawne zmienne {{user_label}} itd.) —
 * ten plik odtwarza dokładnie te same szablony co usunięty
 * Kwantum.LegalMail.Data.Notifications.NotificationMessageBuilder, tylko po stronie klienta.
 *
 * Gdy MessageKey nie jest ustawiony (powiadomienia wpisane ręcznie, np. Maintenance, oraz
 * historyczne wiersze sprzed tej fazy) — używamy po staremu gotowego pola Message.
 */

type Args = Record<string, string | undefined>;

const V = {
  userLabel: "user_label",
  caseClient: "case_client",
  caseSignature: "case_signature",
  caseTitle: "case_title",
  caseLeadingAuthority: "case_leadingAuthority",
  sender: "sender",
  receiver: "receiver",
  documentTitle: "document_title",
  documentSignature: "document_signature",
  version: "version",
  integrationType: "integration_type",
  integrationProvider: "integration_provider",
  spaceUsage: "spaceUsage",
} as const;

const has = (args: Args, key: string) => !!args[key];
const get = (args: Args, key: string) => args[key] ?? "";

function buildTemplate(messageKey: string, args: Args): string | null {
  switch (messageKey) {
    case "NewMessage":
      return `Nowa przesyłka od ${get(args, V.sender)}`;
    case "CaseInvitation":
      return has(args, V.caseSignature)
        ? `Użytkownik ${get(
            args,
            V.userLabel,
          )} zaprasza Cię do wglądu w prowadzoną przez siebie sprawę o sygnaturze ${get(
            args,
            V.caseSignature,
          )}`
        : `Użytkownik ${get(
            args,
            V.userLabel,
          )} zaprasza Cię do wglądu w prowadzoną przez siebie sprawę o tytule ${get(
            args,
            V.caseTitle,
          )}`;
    case "CaseShared":
      return has(args, V.caseSignature)
        ? `Użytkownik ${get(
            args,
            V.userLabel,
          )} udostępnił Ci teczkę o sygnaturze ${get(args, V.caseSignature)}`
        : `Użytkownik ${get(
            args,
            V.userLabel,
          )} udostępnił Ci teczkę o tytule ${get(args, V.caseTitle)}`;
    case "CasePassed":
      return has(args, V.caseSignature)
        ? `Użytkownik ${get(
            args,
            V.userLabel,
          )} przekazał Ci teczkę o sygnaturze ${get(args, V.caseSignature)}`
        : `Użytkownik ${get(
            args,
            V.userLabel,
          )} przekazał Ci teczkę o tytule ${get(args, V.caseTitle)}`;
    case "MessageShared":
      return has(args, V.sender)
        ? `Użytkownik ${get(
            args,
            V.userLabel,
          )} udostępnił Ci przesyłkę od nadawcy ${get(args, V.sender)}`
        : `Użytkownik ${get(
            args,
            V.userLabel,
          )} udostępnił Ci presyłkę o tytule ${get(args, V.documentTitle)}`;
    case "MessagePassed":
      return has(args, V.sender)
        ? `Użytkownik ${get(
            args,
            V.userLabel,
          )} przekazał Ci przesyłkę od nadawcy ${get(args, V.sender)}`
        : `Użytkownik ${get(
            args,
            V.userLabel,
          )} przekazał Ci presyłkę o tytule ${get(args, V.documentTitle)}`;
    case "InvitationAccepted":
      return `Użytkownik ${get(
        args,
        V.userLabel,
      )} zaakceptował Twoje zaproszenie`;
    case "OfficeInvitation":
      return `Użytkownik ${get(
        args,
        V.userLabel,
      )} zaakceptował Twoje zaproszenie o dołączenie do kancelarii`;
    case "SubaccountInvitation":
      return `Użytkownik ${get(
        args,
        V.userLabel,
      )} zaakceptował Twoje zaproszenie ustanawiające jego Twoim subkontem`;
    case "NewPluginVersion":
      return `Dostępna jest nowa wersja wtyczki Noty (wersja ${get(
        args,
        V.version,
      )}). Pobierz nową wersję i dokonaj aktualizacji`;
    case "MessageReceived":
      return `Nadana przez Ciebie przesyłka ${get(
        args,
        V.documentSignature,
      )} została odebrana przez ${get(args, V.receiver)}`;
    case "MessageSendError":
      return `Wystąpił błąd podczas wysyłki nadanej przez Ciebie przesyłki ${get(
        args,
        V.documentSignature,
      )}. Przejdź do szczegółów przesyłki, aby uzyskać więcej informacji.`;
    case "SynchronizationError":
      return `Wystąpił błąd podczas połączenia z zewnętrzną integracją, nazwa integracji: ${get(
        args,
        V.integrationType,
      )}, dostawca: ${get(args, V.integrationProvider)}`;
    case "LowDiskSpace":
      return `Zużycie Twojej powierzchni przechowywania w Nocie wynosi obecnie ${get(
        args,
        V.spaceUsage,
      )}. Przejdź do ustawień aby wyświetlić szczegóły`;
    default:
      return null;
  }
}

function parseArgs(messageArgsJson?: string | null): Args {
  if (!messageArgsJson) return {};
  try {
    const parsed = JSON.parse(messageArgsJson);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

/** Zwraca tekst do wyświetlenia dla powiadomienia niezależnie od tego, czy przyszło z BE ze świeżym MessageKey, czy jest legacy (samo Message). */
export function renderNotificationMessage(notification: {
  MessageKey?: string | null;
  messageKey?: string | null;
  MessageArgsJson?: string | null;
  messageArgsJson?: string | null;
  Message?: string | null;
  message?: string | null;
}): string {
  const messageKey = notification.MessageKey ?? notification.messageKey;
  const legacyMessage = notification.Message ?? notification.message ?? "";

  if (!messageKey) {
    return legacyMessage;
  }

  const args = parseArgs(
    notification.MessageArgsJson ?? notification.messageArgsJson,
  );
  const rendered = buildTemplate(messageKey, args);

  return rendered ?? legacyMessage;
}
